/**
 * Auth-Store (W0.4) — Zustand-basierter Auth-State
 *
 * Token-Storage-Strategie:
 * - Access-Token: in Memory (Zustand-Store), niemals localStorage
 * - Refresh-Token: sessionStorage (Fallback, da Backend kein HttpOnly-Cookie setzt)
 * - "Angemeldet bleiben" (STAGING-FIX 2026-09-02): sessionStorage ist pro Tab —
 *   ein neuer Tab/Browser-Neustart sieht ihn nie, die Checkbox war bislang
 *   folgenlos (defaultChecked, aber nirgends ausgewertet). Ist sie aktiv, wird
 *   der Refresh-Token ZUSAETZLICH in localStorage gespiegelt; getRefreshToken()
 *   liest sessionStorage zuerst, localStorage nur als Fallback. Der Access-Token
 *   bleibt bewusst sessionStorage/Memory-only — der Boot-Silent-Refresh
 *   (main.tsx initAuth) holt sich im neuen Tab ueber den localStorage-Refresh-
 *   Token einen frischen Access-Token.
 *
 * Kompatibilität: smoke.test.tsx nutzt setAuthenticated() — bleibt als Alias erhalten.
 *
 * Erweiterung SWEEP7-E3-FE-1 (F-SW7-14): Expiry-aware Caching
 * - `expiresAt` (Unix-Ms) wird zusaetzlich zum Access-Token persistiert, damit
 *   api-client.ts vor jedem Request pruefen kann ob ein Refresh noetig ist,
 *   statt reaktiv auf jedes 401 zu warten.
 * - Bekannte Repro-Luecke (2026-07-06, live gegen :3927 verifiziert): `POST
 *   /api/v2/auth/login` liefert KEIN `expires_in` (nur `token`/`refresh_token`),
 *   waehrend `POST /oauth/token` (Refresh-Grant) `expires_in: 7200` liefert —
 *   passend zu `access_token_expires_in 2.hours` in
 *   rails_backend_v2/config/initializers/doorkeeper.rb:95. Ohne Fallback wuerde
 *   die erste Navigation nach Login mangels `expiresAt` unnoetig refreshen
 *   (Winston-Auflage). Deshalb: wenn `setTokens` ohne `expiresIn` aufgerufen
 *   wird (Login-Pfad in src/routes/login.tsx, nicht Teil dieser Story),
 *   nehmen wir den bekannten Doorkeeper-Default als Fallback an.
 *   BE-Eskalation empfohlen: `/api/v2/auth/login` sollte `expires_in`
 *   analog zu `/oauth/token` zurueckgeben, damit die FE keine Annahme ueber
 *   die Backend-Konfiguration treffen muss.
 */
import { create } from 'zustand'

const REFRESH_KEY = 'ht-refresh-token'
const ACCESS_KEY = 'ht-access-token'
const EXPIRES_KEY = 'ht-token-expires-at'
// "Angemeldet bleiben": Flag + Refresh-Token-Spiegel in localStorage
const REMEMBER_KEY = 'ht-remember-me'

// FE-ROB-02: Demo-Modus-Keys (Spiegel von services/demoMode.ts — direkt hier
// gehalten, um einen zirkulaeren Import authStore <-> demoMode zu vermeiden).
const DEMO_MODE_KEY = 'ht_demo_mode'
const DEMO_TOKEN_KEY = 'ht_demo_token'

/**
 * FE-ROB-02: Demo-Session hart beenden.
 *
 * Der Demo-Modus hat in api-client.getAuthToken() VORRANG vor dem echten
 * Access-Token. Blieb ht_demo_mode nach einem Logout stehen, lief die
 * anschliessende echte Session weiter unter dem geteilten Demo-Account
 * (fremde Daten sichtbar, Mutationen in den Demo-Account geschrieben).
 * Deshalb wird der Demo-Zustand sowohl bei clearAuth() (jeder Logout-Pfad,
 * inkl. 401-Interceptor) als auch bei setTokens() (echter Login) entfernt.
 */
function clearDemoSession(): void {
  if (typeof window === 'undefined') return
  sessionStorage.removeItem(DEMO_MODE_KEY)
  sessionStorage.removeItem(DEMO_TOKEN_KEY)
}

// Fallback-TTL wenn der Aufrufer keinen expiresIn liefert (Login-Pfad).
// Siehe Kommentar oben — muss mit Doorkeeper-Config synchron gehalten werden.
export const DEFAULT_ACCESS_TOKEN_TTL_SECONDS = 7200

interface AuthState {
  accessToken: string | null
  expiresAt: number | null
  isAuthenticated: boolean
  rememberMe: boolean

  // W0.4 API
  // `remember` (Checkbox "Angemeldet bleiben"): undefined = vorherigen Zustand
  // beibehalten (z.B. Token-Rotation waehrend eines Refreshs).
  setTokens: (accessToken: string, refreshToken?: string, expiresIn?: number, remember?: boolean) => void
  clearAuth: () => void
  getRefreshToken: () => string | null

  // SWEEP7-E3-FE-1: Guard fuer expiry-aware Refresh (api-client.ts)
  // Liefert true wenn ein Access-Token vorhanden UND dessen Ablauf weiter als
  // `bufferMs` in der Zukunft liegt (Default 60s Sicherheitsmarge).
  isAccessTokenFresh: (bufferMs?: number) => boolean

  // Abwärtskompatibilität smoke.test.tsx (W0.1-Smoke)
  setAuthenticated: (v: boolean) => void
}

// Initial-Token aus sessionStorage lesen (W4.x Fix: Page-Reload behaelt Auth)
function readInitialAccessToken(): string | null {
  if (typeof window === 'undefined') return null
  return sessionStorage.getItem(ACCESS_KEY)
}

function readInitialExpiresAt(): number | null {
  if (typeof window === 'undefined') return null
  const raw = sessionStorage.getItem(EXPIRES_KEY)
  if (!raw) return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

// Demo-Modus überlebt den Reload (Fix): demoMode.ts speichert ht_demo_mode/ht_demo_token
// in sessionStorage, aber NICHT unter ACCESS_KEY. Ohne diese Berücksichtigung wirft der
// _auth-Guard Demo-User bei jedem harten Reload/Deep-Link zurück auf /login.
// Direkt aus sessionStorage gelesen (kein Import von demoMode -> vermeidet Circular-Import).
function readInitialDemoActive(): boolean {
  if (typeof window === 'undefined') return false
  return sessionStorage.getItem('ht_demo_mode') === 'true'
}

// "Angemeldet bleiben": Flag lebt in localStorage, ueberlebt also neue Tabs
// und Browser-Neustarts (im Gegensatz zu sessionStorage).
function readInitialRememberMe(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return localStorage.getItem(REMEMBER_KEY) === 'true'
  } catch {
    return false
  }
}

const initialAccess = readInitialAccessToken()
const initialExpiresAt = readInitialExpiresAt()
const initialDemoActive = readInitialDemoActive()
const initialRememberMe = readInitialRememberMe()

export const useAuthStore = create<AuthState>()((set, get) => ({
  accessToken: initialAccess,
  expiresAt: initialExpiresAt,
  isAuthenticated: !!initialAccess || initialDemoActive,
  rememberMe: initialRememberMe,

  setTokens: (accessToken: string, refreshToken?: string, expiresIn?: number, remember?: boolean) => {
    // FE-ROB-02: echter Login beendet eine ggf. noch offene Demo-Session,
    // sonst wuerde getAuthToken() weiterhin den Demo-Token bevorzugen.
    clearDemoSession()
    const ttlSeconds = expiresIn ?? DEFAULT_ACCESS_TOKEN_TTL_SECONDS
    const expiresAt = Date.now() + ttlSeconds * 1000
    // `remember` unset (z.B. Token-Rotation im Refresh-Interceptor) -> bisherigen
    // Zustand beibehalten, statt den Refresh-Token-Spiegel in localStorage zu verlieren.
    const effectiveRemember = remember ?? get().rememberMe
    set({ accessToken, isAuthenticated: true, expiresAt, rememberMe: effectiveRemember })
    try {
      sessionStorage.setItem(ACCESS_KEY, accessToken)
      sessionStorage.setItem(EXPIRES_KEY, String(expiresAt))
    } catch {
      // ignore — Storage nicht verfuegbar (z.B. Private-Mode-Quota)
    }
    if (refreshToken !== undefined) {
      try {
        sessionStorage.setItem(REFRESH_KEY, refreshToken)
      } catch {
        // ignore
      }
      try {
        if (effectiveRemember) {
          localStorage.setItem(REFRESH_KEY, refreshToken)
          localStorage.setItem(REMEMBER_KEY, 'true')
        } else {
          localStorage.removeItem(REFRESH_KEY)
          localStorage.removeItem(REMEMBER_KEY)
        }
      } catch {
        // ignore
      }
    }
  },

  clearAuth: () => {
    // FE-ROB-02: Logout beendet IMMER auch den Demo-Modus (sonst bleibt
    // isAuthenticated nach einem Reload via readInitialDemoActive() true).
    clearDemoSession()
    set({ accessToken: null, isAuthenticated: false, expiresAt: null, rememberMe: false })
    try {
      sessionStorage.removeItem(ACCESS_KEY)
      sessionStorage.removeItem(REFRESH_KEY)
      sessionStorage.removeItem(EXPIRES_KEY)
    } catch {
      // ignore
    }
    try {
      localStorage.removeItem(REFRESH_KEY)
      localStorage.removeItem(REMEMBER_KEY)
    } catch {
      // ignore
    }
  },

  isAccessTokenFresh: (bufferMs = 60_000) => {
    const { accessToken, expiresAt } = get()
    if (!accessToken || expiresAt === null) return false
    return expiresAt - Date.now() > bufferMs
  },

  // sessionStorage zuerst (Tab-lokale Session), localStorage nur als Fallback
  // fuer "Angemeldet bleiben" (neuer Tab / Browser-Neustart).
  getRefreshToken: () => {
    try {
      const fromSession = sessionStorage.getItem(REFRESH_KEY)
      if (fromSession) return fromSession
    } catch {
      // ignore
    }
    try {
      return localStorage.getItem(REFRESH_KEY)
    } catch {
      return null
    }
  },

  // Alias für Abwärtskompatibilität (smoke.test.tsx)
  setAuthenticated: (v: boolean) => set({ isAuthenticated: v }),
}))
